import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";
// import 'font-awesome/css/font-awesome.min.css'; // Importing Font Awesome Icons

const LoanComparison = () => {
  // State variables for Loan Comparison
  // const [loanAmount, setLoanAmount] = useState(0); // Loan Amount
  // const [interestRate, setInterestRate] = useState(0); // Interest Rate
  // const [loanTerm, setLoanTerm] = useState(0); // Loan Term (in years)
  const [monthlyPayment, setMonthlyPayment] = useState(0); // Calculated Monthly Payment
  const [totalCost, setTotalCost] = useState(0); // Total cost of the loan
  const [numLoans, setNumLoans] = useState(1); // Number of loans to compare
  const [loanDetails, setLoanDetails] = useState([{ loanAmount: 0, interestRate: 0, loanTerm: 0 }]); // Loan details for each loan

  // Event handler to update loan details
  const handleLoanDetailChange = (index, field, value) => {
    const updatedLoanDetails = [...loanDetails];
    updatedLoanDetails[index][field] = parseFloat(value) || 0;
    setLoanDetails(updatedLoanDetails);
  };

  // Event handler to add a new loan comparison
  const handleAddLoan = () => {
    setLoanDetails([...loanDetails, { loanAmount: 0, interestRate: 0, loanTerm: 0 }]);
    setNumLoans(numLoans + 1);
  };

  // Event handler to remove a loan comparison
  const handleRemoveLoan = (index) => {
    const updatedLoanDetails = loanDetails.filter((_, i) => i !== index);
    setLoanDetails(updatedLoanDetails);
    setNumLoans(numLoans - 1);
  };

  // Function to calculate monthly payment based on loan details (using standard loan amortization formula)
  const calculateMonthlyPayment = (loanAmount, interestRate, loanTerm) => {
    const rate = interestRate / 100 / 12; // Monthly interest rate
    const numberOfPayments = loanTerm * 12; // Total number of payments (months)
    const monthlyPayment =
      (loanAmount * rate) / (1 - Math.pow(1 + rate, -numberOfPayments));
    return monthlyPayment;
  };

  // useEffect hook to calculate monthly payment and total cost when loan details change
  useEffect(() => {
    let updatedTotalCost = 0;
    loanDetails.forEach((loan) => {
      const monthlyPayment = calculateMonthlyPayment(loan.loanAmount, loan.interestRate, loan.loanTerm);
      updatedTotalCost += monthlyPayment * loan.loanTerm * 12;
    });
    setMonthlyPayment(loanDetails[0] ? calculateMonthlyPayment(loanDetails[0].loanAmount, loanDetails[0].interestRate, loanDetails[0].loanTerm) : 0);
    setTotalCost(updatedTotalCost);
  }, [loanDetails]);

  // Function to handle NaN values and display as 0.00
  const formatToZeroIfNaN = (value) => {
    return isNaN(value) ? "0.00" : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Layout title="Loan Comparison Calculator" path="loan-comparison-calculator">
      <section className="section-padding" id="loan-comparison-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Loan Comparison Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Loan Comparison Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Loop through each loan and create inputs for loan amount, interest rate, and loan term */}
                    {loanDetails.map((loan, index) => (
                      <div className="col-md-6 col-lg-4" key={index}>
                        <label htmlFor={`loanAmount-${index}`} className="form-label">
                          Loan Amount {index + 1}
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id={`loanAmount-${index}`}
                          placeholder="Enter loan amount"
                          value={loan.loanAmount || ""}
                          onChange={(e) => handleLoanDetailChange(index, "loanAmount", e.target.value)}
                        />
                        <label htmlFor={`interestRate-${index}`} className="form-label mt-2">
                          Interest Rate {index + 1} (%)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id={`interestRate-${index}`}
                          placeholder="Enter interest rate"
                          value={loan.interestRate || ""}
                          onChange={(e) => handleLoanDetailChange(index, "interestRate", e.target.value)}
                        />
                        <label htmlFor={`loanTerm-${index}`} className="form-label mt-2">
                          Loan Term {index + 1} (years)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id={`loanTerm-${index}`}
                          placeholder="Enter loan term in years"
                          value={loan.loanTerm || ""}
                          onChange={(e) => handleLoanDetailChange(index, "loanTerm", e.target.value)}
                        />
                        {/* Button to remove loan comparison as an icon */}
                        <button
                          type="button"
                          className="btn btn-danger mt-2"
                          onClick={() => handleRemoveLoan(index)}
                        >
                          <i className="fa fa-trash"></i> 
                        </button>
                      </div>
                    ))}

                    {/* Button to add another loan with custom color */}
                    <div className="col-md-12 mt-3">
                      <button 
                        type="button" 
                        className="btn" 
                        onClick={handleAddLoan}
                        style={{ color: '#ffffff', backgroundColor: '#13547a' }} // Custom button color
                      >
                        <i className="fa fa-plus"></i> 
                      </button>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Cost of Loans:{" "}
                        <span className="fw-bold ms-3">
                          {formatToZeroIfNaN(totalCost)}
                        </span>
                      </h6>
                      <p>
                        Monthly Payment: <strong>{formatToZeroIfNaN(monthlyPayment)}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Loan Comparison Calculator
                </h3>

                {/* What is a Loan Comparison Calculator? */}
                <h4 className="mt-3 mb-3">What is a Loan Comparison Calculator?</h4>
                <p>
                  The **Loan Comparison Calculator** allows users to compare the terms of multiple loans, including monthly payments, interest rates, and total cost, helping them make the most informed decision about borrowing.
                </p>

                {/* Why Use a Loan Comparison Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Loan Comparison Calculator?</h4>
                <p>
                  Comparing loan terms is essential when selecting the right loan. This calculator helps borrowers understand the differences in monthly payments, interest rates, and total loan costs, ensuring they choose the best option for their financial situation.
                </p>

                {/* Benefits of Using a Loan Comparison Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Loan Comparison Calculator</h4>
                <ul>
                  <li><strong>Helps borrowers find the most affordable loan:</strong> By comparing various loan terms, users can easily identify the best loan option for their needs.</li>
                  <li><strong>Simplifies comparison of loan terms:</strong> The calculator allows borrowers to quickly compare key factors like interest rates, loan terms, and monthly payments.</li>
                  <li><strong>Useful for debt management and refinancing:</strong> It aids in evaluating refinancing options and managing existing debt more efficiently.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Loan Comparison Calculator?</h4>
                <p>
                  The Loan Comparison Calculator is perfect for:
                </p>
                <ul>
                  <li><strong>Individuals:</strong> To make informed decisions about personal loans, mortgages, and auto loans.</li>
                  <li><strong>Real estate professionals:</strong> For helping clients choose the best mortgage options.</li>
                  <li><strong>Financial advisors:</strong> To guide clients in making the most cost-effective borrowing decisions.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">Can I Compare Different Types of Loans?</h4>
                <p>
                  Yes, the Loan Comparison Calculator can be used to compare various types of loans, such as mortgages, auto loans, personal loans, and student loans.
                </p>

                <h4 className="mt-4 mb-3">How Does the Loan Term Affect the Total Cost?</h4>
                <p>
                  A longer loan term generally results in smaller monthly payments but increases the total cost of the loan, as interest is paid over a longer period.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LoanComparison;
